import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, X } from 'lucide-react';
import { API } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { inputCls, formatErr } from '../billing/shared';

const Recipients = ({ list, onChange, testid }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const parts = draft.split(/[\s,;]+/).map((x) => x.trim().toLowerCase()).filter((x) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x));
    if (parts.length) onChange([...new Set([...list, ...parts])]);
    setDraft('');
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {list.map((e) => (
        <span key={e} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700" data-testid={`${testid}-chip`}>
          {e}<button type="button" onClick={() => onChange(list.filter((x) => x !== e))} className="text-slate-400 hover:text-red-600" aria-label={`Remove ${e}`}><X size={12} /></button>
        </span>
      ))}
      <input value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={add} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }}
        placeholder="Add email…" className={`${inputCls} w-48 py-1 text-xs`} data-testid={`${testid}-input`} />
    </div>
  );
};

const Toggle = ({ on, onClick, testid }) => (
  <button type="button" role="switch" aria-checked={on} onClick={onClick} className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${on ? 'bg-teal-500' : 'bg-slate-300'}`} data-testid={testid}>
    <span className={`absolute left-0 top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${on ? 'translate-x-4' : 'translate-x-0.5'}`} />
  </button>
);

export const DailyEmailsCard = ({ data, onSaved }) => {
  const { authHeader } = useAuth();
  const [reports, setReports] = useState(data.reports);
  const [team, setTeam] = useState(data.team_confirmation);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(''); const [ok, setOk] = useState('');
  const upd = (key, patch) => setReports((r) => ({ ...r, [key]: { ...r[key], ...patch } }));
  const save = async () => {
    setBusy(true); setErr(''); setOk('');
    try {
      const { data: d } = await axios.put(`${API}/settings/email/reports`, { reports, team_confirmation: team }, { headers: authHeader() });
      onSaved(d); setOk('Saved.');
    } catch (e) { setErr(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="daily-emails-section">
      <div>
        <h2 className="text-sm font-semibold text-teal-700 uppercase tracking-wider">Team emails</h2>
        <p className="text-xs text-slate-500 mt-1">Choose who gets each daily email. Daily emails go out each morning (IST) once your email is connected.</p>
      </div>
      <div className="divide-y divide-slate-100">
        {Object.entries(data.report_labels).map(([key, label]) => (
          <div key={key} className="py-3 space-y-2" data-testid={`report-row-${key}`}>
            <div className="flex items-center gap-3">
              <Toggle on={reports[key].enabled} onClick={() => upd(key, { enabled: !reports[key].enabled })} testid={`report-toggle-${key}`} />
              <span className="text-sm font-medium text-slate-900">{label}</span>
            </div>
            {reports[key].enabled && <Recipients list={reports[key].recipients} onChange={(l) => upd(key, { recipients: l })} testid={`report-recipients-${key}`} />}
          </div>
        ))}
        <div className="py-3 space-y-1.5" data-testid="team-confirmation-row">
          <div className="flex items-center gap-3">
            <Toggle on={team} onClick={() => setTeam(!team)} testid="team-confirmation-toggle" />
            <span className="text-sm font-medium text-slate-900">Appointment confirmed — email the team</span>
          </div>
          <p className="text-xs text-slate-500 pl-12" data-testid="team-confirmation-recipients">
            Goes to everyone with access to Appointments{data.team_recipients.length ? `: ${data.team_recipients.join(', ')}` : ' (nobody yet)'}.
          </p>
        </div>
      </div>
      {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2.5 py-1.5" data-testid="reports-error">{err}</div>}
      {ok && <div className="text-xs text-emerald-700" data-testid="reports-ok">{ok}</div>}
      <button type="button" onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white text-sm font-semibold px-4 py-2 rounded-lg" data-testid="reports-save-btn">
        {busy && <Loader2 size={14} className="animate-spin" />} Save team emails
      </button>
    </section>
  );
};
